import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ExternalLink, Play } from 'lucide-react'
import { useState } from 'react'

import { Alert } from '@/components/feedback/Alert'
import { ErrorState } from '@/components/feedback/ErrorState'
import { Loading } from '@/components/feedback/Loading'
import { useToastStack } from '@/components/feedback/toast-stack'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Progress } from '@/components/ui/progress'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useQueryState } from '@/hooks/use-query-state'
import { EMPTY_VALUE, formatDateTime, formatTime } from '@/lib/format'
import { processHealth, waitingFor } from '@/lib/hr'
import { messages } from '@/lib/messages'
import { toProblem } from '@/lib/problem'
import { queryKeys } from '@/lib/query-keys'
import { hrApi, type HrFlowRun, type HrProcess } from '@/services/http/hr'
import { HrPageHeader } from '@/features/hr/HrPageHeader'
import { useAuthStore } from '@/stores/auth-store'

const t = messages.hr
const p = t.processes

function stateTone(state: string | null | undefined) {
  if (state === 'COMPLETED') return 'success' as const
  if (state === 'FAILED' || state === 'CRASHED') return 'danger' as const
  if (state === 'RUNNING' || state === 'PENDING') return 'info' as const
  return 'neutral' as const
}

function RunState({ run }: { run: HrFlowRun }) {
  return (
    <Badge size="sm" tone={stateTone(run.state_type)}>
      {run.state_name ?? p.state[run.state_type ?? ''] ?? run.state_type}
    </Badge>
  )
}

function duration(run: HrFlowRun | null | undefined): string {
  return run?.duration_seconds ? p.seconds(run.duration_seconds) : EMPTY_VALUE
}

/**
 * Çalışan run'ın tahmini ilerlemesi: geçen süre / son başarılı çalışmanın süresi. Tahmin
 * aşılırsa %95'te bekler (bitişi Prefect bildirir).
 */
function runningProgress(process: HrProcess): number {
  const started = process.running[0]?.start_time
  const estimate = process.last_run?.duration_seconds
  if (!started || !estimate) return 5
  const elapsed = (Date.now() - new Date(started).getTime()) / 1000
  return Math.max(5, Math.min(95, (elapsed / estimate) * 100))
}

function HealthBadge({ process }: { process: HrProcess }) {
  const health = processHealth(process)
  if (health === 'failed') return <Badge tone="danger">{p.failedBadge}</Badge>
  if (health === 'overdue') return <Badge tone="warning">{p.overdueBadge}</Badge>
  if (health === 'ok') return <Badge tone="success">{p.okBadge}</Badge>
  return null
}

function ProcessCard({
  process,
  configured,
  onStarted,
}: {
  process: HrProcess
  configured: boolean
  /** Başlatılan run'ın birkaç saniyede kartta görünmesi için sayfa kısa süre sık yenilenir. */
  onStarted: () => void
}) {
  const queryClient = useQueryClient()
  const { toast } = useToastStack()
  const canRun = useAuthStore((state) => state.hasPermission('hr.action:run'))
  const [confirming, setConfirming] = useState(false)
  const name = p.names[process.key] ?? process.key
  const run = useMutation({
    mutationFn: () => hrApi.runProcess(process.key),
    onSuccess: () => {
      setConfirming(false)
      onStarted()
      toast({ type: 'success', title: p.runStarted(name) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.hr.processes() })
      void queryClient.invalidateQueries({ queryKey: queryKeys.hr.processRuns(process.key) })
    },
    onError: (error) =>
      toast({
        type: 'error',
        title: p.runFailed,
        description: toProblem(error).detail ?? undefined,
      }),
  })
  const last = process.last_run
  const next = process.next_run
  return (
    <Card className="flex flex-col gap-3 p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex flex-col gap-1">
          <h2 className="text-sm font-medium">{name}</h2>
          {!process.found && (
            <span className="text-text-muted text-xs">
              {configured ? p.notFound : p.notConfiguredCard}
            </span>
          )}
        </div>
        <HealthBadge process={process} />
      </div>
      {process.found && (
        <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1.5 text-sm">
          <dt className="text-text-muted">{p.lastRun}</dt>
          <dd className="flex flex-wrap items-center gap-2">
            {last ? (
              <>
                <span className="tabular-nums">{formatDateTime(last.start_time)}</span>
                <RunState run={last} />
                <span className="text-text-muted text-xs">{duration(last)}</span>
              </>
            ) : (
              p.never
            )}
          </dd>
          <dt className="text-text-muted">{p.nextRun}</dt>
          <dd className="tabular-nums">
            {process.paused
              ? p.paused
              : !process.scheduled
                ? p.notScheduled
                : next
                  ? formatDateTime(next.expected_start_time)
                  : EMPTY_VALUE}
          </dd>
        </dl>
      )}
      {process.running.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <span className="text-brand text-xs">
            {p.running} · {formatTime(process.running[0].start_time)}
          </span>
          <Progress aria-label={p.running} value={runningProgress(process)} />
        </div>
      )}
      <div className="mt-auto flex flex-wrap gap-2">
        {process.runnable && canRun && process.found && (
          <Button
            size="sm"
            disabled={run.isPending || process.running.length > 0}
            onClick={() => setConfirming(true)}
          >
            <Play /> {p.runNow}
          </Button>
        )}
        <Dialog open={confirming} onOpenChange={setConfirming}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{p.runConfirmTitle(name)}</DialogTitle>
              <DialogDescription>{p.runConfirmBody}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="secondary" onClick={() => setConfirming(false)}>
                {t.actions.cancel}
              </Button>
              <Button disabled={run.isPending} onClick={() => run.mutate()}>
                <Play /> {p.runNow}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {process.url && (
          <Button
            size="sm"
            variant="ghost"
            render={<a href={process.url} target="_blank" rel="noreferrer" />}
            nativeButton={false}
          >
            <ExternalLink /> {p.openPrefect}
          </Button>
        )}
      </div>
    </Card>
  )
}

/** `ik-panel-aksiyon` kuyruğu: akışın henüz almadığı aksiyonlar (Prefect'ten bağımsız, app_db). */
function QueueCard() {
  const query = useQuery({
    queryKey: queryKeys.hr.queue(),
    queryFn: hrApi.queue,
    refetchInterval: 15_000,
  })
  const data = query.data
  return (
    <Card className="flex flex-col gap-2 p-4">
      <h2 className="text-sm font-medium">{p.queue}</h2>
      <p className="text-text-muted text-xs">{p.queueHint}</p>
      {data && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <Badge size="sm" tone={data.depth === 0 ? 'success' : 'info'}>
            {p.queueDepth(data.depth)}
          </Badge>
          {data.oldest_requested_at && (
            <span className="text-text-muted text-xs">
              {p.queueOldest(waitingFor(data.oldest_requested_at))}
            </span>
          )}
        </div>
      )}
    </Card>
  )
}

function RunsTable({ processKey }: { processKey: string }) {
  const query = useQuery({
    queryKey: queryKeys.hr.processRuns(processKey),
    queryFn: () => hrApi.processRuns(processKey),
    refetchInterval: 30_000,
  })
  if (query.isLoading) return <Loading rows={4} />
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />
  return (
    <div className="border-border rounded-panel bg-surface overflow-x-auto border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{p.runColumns.name}</TableHead>
            <TableHead>{p.runColumns.state}</TableHead>
            <TableHead>{p.runColumns.start}</TableHead>
            <TableHead>{p.runColumns.duration}</TableHead>
            <TableHead>{p.runColumns.link}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {(query.data ?? []).map((run) => (
            <TableRow key={run.id}>
              <TableCell>{run.name}</TableCell>
              <TableCell>
                <RunState run={run} />
              </TableCell>
              <TableCell className="tabular-nums">
                {formatDateTime(run.start_time ?? run.expected_start_time)}
              </TableCell>
              <TableCell className="tabular-nums">{duration(run)}</TableCell>
              <TableCell>
                {run.url && (
                  <a
                    href={run.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-brand inline-flex items-center gap-1 text-xs underline-offset-2 hover:underline"
                  >
                    <ExternalLink className="size-3" /> {p.openPrefect}
                  </a>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

/** "Şimdi çalıştır"dan sonra bu süre boyunca sayfa 3 sn'de bir yenilenir (run kartta görünsün). */
const FAST_POLL_MS = 30_000

export function ProcessesPage() {
  const state = useQueryState()
  const [fastUntil, setFastUntil] = useState(0)
  const query = useQuery({
    queryKey: queryKeys.hr.processes(),
    queryFn: hrApi.processes,
    refetchInterval: () => (Date.now() < fastUntil ? 3000 : 30_000),
  })
  const data = query.data
  const runsKey = state.get('surec') ?? 'giris_cikis'

  return (
    <div className="flex flex-col gap-4">
      <HrPageHeader
        title={p.title}
        description={p.description}
        actions={
          data && (
            <span className="text-text-muted text-xs">
              {p.checkedAt(formatTime(data.checked_at))}
            </span>
          )
        }
      />
      {query.isLoading && <Loading rows={4} />}
      {query.isError && <ErrorState error={query.error} onRetry={() => void query.refetch()} />}
      {data && !data.configured && <Alert tone="warning" title={p.notConfigured} />}
      {data?.configured && !data.reachable && (
        <Alert tone="warning" title={p.unreachable}>
          {p.unreachableBody}
        </Alert>
      )}
      {data && (
        <>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {data.processes.map((process) => (
              <ProcessCard
                key={process.key}
                process={process}
                configured={data.configured}
                onStarted={() => setFastUntil(Date.now() + FAST_POLL_MS)}
              />
            ))}
            <Card className="flex flex-col gap-2 p-4">
              <h2 className="text-sm font-medium">{p.health}</h2>
              <p className="text-text-muted text-xs">{p.healthHint}</p>
              {data.fetch_health === null || data.fetch_health === undefined ? (
                <p className="text-warning text-sm">{p.healthUnavailable}</p>
              ) : (
                <ul className="flex flex-col gap-1.5 text-sm">
                  {data.fetch_health.map((item) => (
                    <li key={item.tablo_adi} className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{item.tablo_adi}</span>
                      <Badge
                        size="sm"
                        tone={(item.ardisik_sifir ?? 0) >= 2 ? 'warning' : 'success'}
                      >
                        {p.healthZero(item.ardisik_sifir ?? 0)}
                      </Badge>
                      <span className="text-text-muted text-xs tabular-nums">
                        {formatDateTime(item.son_calisma)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
            <QueueCard />
          </div>
          <section className="flex flex-col gap-3">
            <h2 className="text-sm font-medium">{p.runs}</h2>
            <Tabs value={runsKey} onValueChange={(value: string) => state.set({ surec: value })}>
              <TabsList>
                {data.processes.map((process) => (
                  <TabsTrigger key={process.key} value={process.key}>
                    {p.names[process.key] ?? process.key}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            <RunsTable processKey={runsKey} />
          </section>
        </>
      )}
    </div>
  )
}
